import { useEffect, useState, useRef } from 'react'
import { Link } from 'react-router-dom'
import {
  Plus, Upload, ChevronDown, ChevronRight,
  Trash2, X, Building2, TrendingUp,
  Landmark, Coins, RefreshCw, Check, Pencil, Save, History,
} from 'lucide-react'
import { api } from '@/lib/api'
import {
  PieChart, Pie, Cell, Tooltip, ResponsiveContainer,
} from 'recharts'
import { useAccounts } from '@/hooks/useAccounts'
import { useAuth } from '@/hooks/useAuth'
import { clsx } from 'clsx'
import ExportCSV, { type ColumnDef } from '@/components/ui/ExportCSV'
import { IdentifierEditor } from '@/components/accounts/IdentifierEditor'
import { DataQualityPanel } from '@/components/accounts/DataQualityPanel'
import { PricingReviewCard } from '@/components/accounts/PricingReviewCard'
import { PerformanceStrip } from '@/components/accounts/PerformanceStrip'
import TrendBadge from '@/components/ui/TrendBadge'
import PageHeader from '@/components/ui/PageHeader'
import type { Account, Holding, CSVImportResponse, LivePnlResponse, ReconciliationFlag } from '@/lib/types'
import { labelAssetType, labelBondSector } from '@/lib/assetLabels'
// Native-currency symbols for per-share price/cost columns — a $395.60 stock
// must not read as £395.60 (AJ Bell XO, 2026-07-16) — live in lib/format's
// fmtMoney/ccySymbol. Values stay GBP.
import { fmtGBP, fmtGBPSigned, fmtMoney, fmtPct, fmtPctSigned, fmtAgo } from '@/lib/format'

// Per-account trend snapshot derived from /api/pnl/live
interface AccountTrend {
  today_gbp: number
  today_pct: number | null
  pnl_pct: number | null
}

/** Case-insensitive cash-like classifier for holdings.
 *
 * The Add Holding dropdown historically stored 'Cash' (title case) while the
 * edit path compared === 'cash', so cash rows silently fell back to the
 * qty × price editor — the exact UI that produced the £2.5B typo. Exported
 * so the contract stays pinned by tests. */
export function isCashLikeAssetType(assetType: string | null | undefined): boolean {
  const t = (assetType ?? '').trim().toLowerCase().replace(/\s+/g, '_')
  return t === 'cash' || t === 'real_estate' || t === 'liability'
}

/** Parse an edited quantity string. Returns the parsed number when it is a
 * real finite number (INCLUDING 0 — "set my position to zero" must stick),
 * otherwise the previous quantity. Exported so the contract stays pinned. */
export function quantityPatchValue(input: string, previous: number): number {
  const q = parseFloat(input)
  return Number.isFinite(q) ? q : previous
}

const COLORS = ['#3b82f6', '#f59e0b', '#8b5cf6', '#10b981', '#06b6d4', '#f43f5e', '#e879f9', '#14b8a6']

const PROVIDERS = [
  { value: 'ig_index',             label: 'IG Index' },
  { value: 'robinhood',            label: 'Robinhood' },
  { value: 'ibkr',                 label: 'IBKR' },
  { value: 'aj_bell',              label: 'AJ Bell' },
  { value: 'hargreaves_lansdown',  label: 'Hargreaves Lansdown' },
  { value: 'coinbase',             label: 'Coinbase' },
  { value: 'manual',               label: 'Manual / Other' },
]

const ACCOUNT_TYPES = [
  'Trading', 'ISA', 'SIPP', 'GIA', 'Options', 'Crypto', 'Discretionary',
  'Savings', 'Bank', 'Real Estate', 'Liabilities',
]

const ASSET_TYPES = [
  'Stock', 'ETF', 'Fund', 'Bond', 'Crypto', 'Cash', 'Option', 'Other',
]

const PROVIDER_ICONS: Record<string, typeof Building2> = {
  ig_index: TrendingUp,
  robinhood: TrendingUp,
  ibkr: TrendingUp,
  aj_bell: Landmark,
  hargreaves_lansdown: Landmark,
  coinbase: Coins,
  manual: Coins,
}

function providerLabel(provider: string) {
  return PROVIDERS.find(p => p.value === provider)?.label ?? provider
}

// ─────────────────────────────────────────────────────────────────────────────
// Add Account Modal
// ─────────────────────────────────────────────────────────────────────────────

interface IgPasteModalProps {
  accounts: Account[]
  onClose: () => void
}

function IgPasteModal({ accounts, onClose }: IgPasteModalProps) {
  const igAccounts = accounts.filter(a => a.provider === 'ig_index')
  const [accountId, setAccountId] = useState<number | null>(
    igAccounts.find(a => /smart/i.test(a.name))?.id ?? igAccounts[0]?.id ?? null,
  )
  const [text, setText] = useState('')
  const [busy, setBusy] = useState(false)
  const [result, setResult] = useState<any>(null)
  const [error, setError] = useState<string | null>(null)

  const submit = async () => {
    if (!accountId || !text.trim()) return
    setBusy(true)
    setError(null)
    setResult(null)
    try {
      const res = await fetch(`/api/accounts/${accountId}/import-ig-paste`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('av-access-token') || ''}`,
        },
        body: JSON.stringify({ text }),
      })
      if (!res.ok) {
        const msg = await res.text()
        setError(`Import failed: ${msg.slice(0, 300)}`)
      } else {
        setResult(await res.json())
      }
    } catch (e: any) {
      setError(String(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Paste IG positions dialog"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
    >
      <div className="card w-full max-w-2xl p-5 space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-bold text-text-primary">Paste IG positions</h2>
          <button
            type="button"
            onClick={onClose}
            className="text-text-muted hover:text-text-primary text-lg"
            aria-label="Close"
            title="Close"
          >
            ×
          </button>
        </div>
        <p className="text-xs text-text-muted leading-snug">
          In the IG mobile app, open your positions list, tap-hold → "Select all" →
          "Copy". Paste the text below. The importer extracts name, quantity,
          book cost, P&L and value for each position. This replaces the account's
          existing holdings.
        </p>

        <label className="block text-xs text-text-muted">
          Target account:
          <select
            value={accountId ?? ''}
            onChange={(e) => setAccountId(parseInt(e.target.value))}
            className="mt-1 w-full px-3 py-2 rounded-lg border border-border bg-surface-card text-sm text-text-primary"
          >
            {igAccounts.map(a => (
              <option key={a.id} value={a.id}>{a.name}</option>
            ))}
            {igAccounts.length === 0 && <option value="">No IG accounts found</option>}
          </select>
        </label>

        <label className="block text-xs text-text-muted">
          Paste here:
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={'iShares Core FTSE 100 UCITS ETF (Dist)\n+207.4148901\n£1,663.17\n£505.97\n£2,169.14\n\niShares Physical Gold ETC - SGLN\n+30.82\n£1,383.74\n£765.15\n£2,148.89'}
            rows={14}
            className="mt-1 w-full px-3 py-2 rounded-lg border border-border bg-surface-card text-xs font-mono text-text-primary focus:outline-none focus:ring-1 focus:ring-blue"
          />
        </label>

        {error && (
          <div className="text-xs text-rose-400 border border-rose-500/30 bg-rose-500/10 rounded p-2">
            {error}
          </div>
        )}
        {result && (
          <div className="text-xs text-emerald-400 border border-emerald-500/30 bg-emerald-500/10 rounded p-2">
            ✓ Imported {result.imported} positions · total £{result.total_value?.toLocaleString?.('en-GB', { maximumFractionDigits: 0 })}
            {result.skipped?.length > 0 && (
              <div className="text-amber-400 mt-1">
                Skipped: {result.skipped.join(', ')}
              </div>
            )}
          </div>
        )}

        <div className="flex items-center justify-end gap-2">
          <button type="button" onClick={onClose} className="btn-secondary">
            Close
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={busy || !accountId || !text.trim()}
            className="btn-primary"
          >
            {busy ? 'Importing…' : 'Import'}
          </button>
        </div>
      </div>
    </div>
  )
}

function AddAccountModal({ onClose }: { onClose: () => void }) {
  const { createAccount } = useAccounts()
  const [name, setName] = useState('')
  const [provider, setProvider] = useState('manual')
  const [accountType, setAccountType] = useState('GIA')
  const [currency, setCurrency] = useState('GBP')
  const [owner, setOwner] = useState('Unman')
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!name.trim()) return
    setSaving(true)
    setSaveError(null)
    const created = await createAccount({
      name: name.trim(),
      provider,
      account_type: accountType,
      currency,
      owner: owner.trim() || 'Unman',
    })
    setSaving(false)
    if (!created) {
      // Keep the modal open — closing silently would look like success.
      setSaveError('Account was NOT created — the server rejected the request. Check your connection and try again.')
      return
    }
    onClose()
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Add account dialog"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
    >
      <form onSubmit={handleSubmit} className="card w-full max-w-sm sm:max-w-md p-6 animate-fade-in space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="font-bold text-text-primary text-lg">Add Account</h2>
          <button type="button" onClick={onClose} className="btn-ghost p-1.5" aria-label="Close dialog">
            <X size={18} />
          </button>
        </div>

        <div>
          <label className="block text-xs text-text-muted mb-1" htmlFor="acc-name">Account Name</label>
          <input
            id="acc-name"
            className="input w-full"
            placeholder="e.g. AJ Bell ISA"
            value={name}
            onChange={e => setName(e.target.value)}
            required
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs text-text-muted mb-1" htmlFor="acc-provider">Provider</label>
            <select id="acc-provider" className="input w-full" value={provider} onChange={e => setProvider(e.target.value)}>
              {PROVIDERS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs text-text-muted mb-1" htmlFor="acc-type">Account Type</label>
            <select id="acc-type" className="input w-full" value={accountType} onChange={e => setAccountType(e.target.value)}>
              {ACCOUNT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs text-text-muted mb-1" htmlFor="acc-currency">Currency</label>
            <select id="acc-currency" className="input w-full" value={currency} onChange={e => setCurrency(e.target.value)}>
              <option value="GBP">GBP</option>
              <option value="USD">USD</option>
              <option value="EUR">EUR</option>
            </select>
          </div>
          <div>
            <label className="block text-xs text-text-muted mb-1" htmlFor="acc-owner">Owner</label>
            <input
              id="acc-owner"
              className="input w-full"
              placeholder="e.g. Unman"
              value={owner}
              onChange={e => setOwner(e.target.value)}
            />
          </div>
        </div>

        {saveError && (
          <div className="text-xs text-rose-light border border-rose/40 bg-rose/10 rounded p-2" role="alert">
            {saveError}
          </div>
        )}

        <button type="submit" disabled={saving || !name.trim()} className="btn-primary w-full gap-2">
          {saving ? <RefreshCw size={14} className="animate-spin" /> : <Plus size={14} />}
          {saving ? 'Creating...' : 'Create Account'}
        </button>
      </form>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Add Holding Modal
// ─────────────────────────────────────────────────────────────────────────────

interface AddHoldingModalProps {
  account: Account
  onClose: () => void
}

function AddHoldingModal({ account, onClose }: AddHoldingModalProps) {
  const { addHolding } = useAccounts()
  // Cash accounts (Bank, Savings, Premium Bonds) get a simplified form:
  // one "Amount £" input instead of qty × price. The qty * price model
  // is the canonical bug here — typing 50000 in both fields → £2.5B.
  const isCashAccount = (() => {
    const t = (account.account_type || '').toLowerCase()
    const n = (account.name || '').toLowerCase()
    return ['bank', 'savings', 'premium bonds', 'cash', 'money market'].includes(t)
      || n.includes('premium bond')
  })()

  const [name, setName] = useState(isCashAccount ? account.name || '' : '')
  const [symbol, setSymbol] = useState('')
  const [quantity, setQuantity] = useState('')
  const [avgCost, setAvgCost] = useState('')
  const [currentPrice, setCurrentPrice] = useState('')
  const [currency, setCurrency] = useState(account.currency)
  const [assetType, setAssetType] = useState(isCashAccount ? 'cash' : '')
  const [notes, setNotes] = useState('')
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)
  // Cash-only state: a single "amount" input. Stored in pounds.
  const [cashAmount, setCashAmount] = useState('')

  const computedValue = () => {
    if (isCashAccount) {
      return parseFloat(cashAmount) || 0
    }
    const qty = parseFloat(quantity) || 0
    const price = parseFloat(currentPrice) || parseFloat(avgCost) || 0
    return qty * price
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!name.trim()) return
    // Cash accounts only need the amount; non-cash needs quantity.
    if (isCashAccount && !cashAmount) return
    if (!isCashAccount && !quantity) return
    setSaving(true)
    setSaveError(null)
    const amount = computedValue()
    const ok = await addHolding(account.id, {
      name: name.trim(),
      symbol: symbol.trim() || undefined,
      // For cash: qty = value, price = 1.00 so the qty * price invariant
      // holds and "cash is cash" — no FX rescaling, no silly mults.
      quantity: isCashAccount ? amount : parseFloat(quantity),
      avg_cost: isCashAccount ? 1.00 : (avgCost ? parseFloat(avgCost) : undefined),
      current_price: isCashAccount ? 1.00 : (currentPrice ? parseFloat(currentPrice) : undefined),
      value: amount,
      currency,
      asset_type: assetType || undefined,
      notes: notes.trim() || undefined,
    })
    setSaving(false)
    if (!ok) {
      // Keep the modal open — closing silently would look like success.
      setSaveError('Holding was NOT saved — the server rejected the request. Nothing was added; try again.')
      return
    }
    onClose()
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Add holding dialog"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
      onClick={e => { if (e.target === e.currentTarget) onClose() }}
    >
      <form
        onSubmit={handleSubmit}
        className="card w-full max-w-lg p-6 animate-fade-in space-y-4"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between">
          <div>
            <h2 className="font-bold text-text-primary text-lg">Add Holding</h2>
            <p className="text-xs text-text-muted">{account.name}</p>
          </div>
          <button type="button" onClick={onClose} className="btn-ghost p-1.5" aria-label="Close dialog">
            <X size={18} />
          </button>
        </div>

        {isCashAccount ? (
          // ── Simplified cash form — one Amount £ input, no qty × price ──
          <div className="space-y-3">
            <div>
              <label className="block text-xs text-text-muted mb-1" htmlFor="h-name">Name *</label>
              <input
                id="h-name"
                className="input w-full"
                placeholder="e.g. Premium Bonds"
                value={name}
                onChange={e => setName(e.target.value)}
                required
              />
            </div>
            <div>
              <label className="block text-xs text-text-muted mb-1" htmlFor="h-cash-amount">
                Amount ({currency}) *
              </label>
              <input
                id="h-cash-amount"
                type="number"
                step="any"
                inputMode="decimal"
                className="input w-full font-mono text-lg"
                placeholder="50000"
                value={cashAmount}
                onChange={e => setCashAmount(e.target.value)}
                required
                autoFocus
              />
              <p className="text-[11px] text-text-muted mt-1">
                Cash is cash — just type the total. No quantity × price needed.
              </p>
            </div>
            <div>
              <label className="block text-xs text-text-muted mb-1" htmlFor="h-cash-currency">Currency</label>
              <select id="h-cash-currency" className="input w-full" value={currency} onChange={e => setCurrency(e.target.value)}>
                <option value="GBP">GBP</option>
                <option value="USD">USD</option>
                <option value="EUR">EUR</option>
              </select>
            </div>
            <div>
              <label className="block text-xs text-text-muted mb-1" htmlFor="h-notes">Notes</label>
              <input
                id="h-notes"
                className="input w-full"
                placeholder="Optional notes"
                value={notes}
                onChange={e => setNotes(e.target.value)}
              />
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <label className="block text-xs text-text-muted mb-1" htmlFor="h-name">Name *</label>
              <input
                id="h-name"
                className="input w-full"
                placeholder="e.g. Apple Inc"
                value={name}
                onChange={e => setName(e.target.value)}
                required
              />
            </div>
            <div>
              <label className="block text-xs text-text-muted mb-1" htmlFor="h-symbol">Symbol</label>
              <input
                id="h-symbol"
                className="input w-full font-mono"
                placeholder="e.g. AAPL"
                value={symbol}
                onChange={e => setSymbol(e.target.value.toUpperCase())}
              />
            </div>
            <div>
              <label className="block text-xs text-text-muted mb-1" htmlFor="h-asset-type">Asset Type</label>
              {/* Values stored lowercase — the rest of the app (isCashLike,
                  badge rendering, backend classifiers) compares lowercase.
                  Storing 'Cash' here bypassed the cash editor entirely. */}
              <select id="h-asset-type" className="input w-full" value={assetType} onChange={e => setAssetType(e.target.value)}>
                <option value="">— select —</option>
                {ASSET_TYPES.map(t => <option key={t} value={t.toLowerCase()}>{t}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs text-text-muted mb-1" htmlFor="h-qty">Quantity *</label>
              <input
                id="h-qty"
                type="number"
                step="any"
                className="input w-full font-mono"
                placeholder="0"
                value={quantity}
                onChange={e => setQuantity(e.target.value)}
                required
              />
            </div>
            <div>
              <label className="block text-xs text-text-muted mb-1" htmlFor="h-avg-cost">Avg Cost</label>
              <input
                id="h-avg-cost"
                type="number"
                step="any"
                className="input w-full font-mono"
                placeholder="0.00"
                value={avgCost}
                onChange={e => setAvgCost(e.target.value)}
              />
            </div>
            <div>
              <label className="block text-xs text-text-muted mb-1" htmlFor="h-price">Current Price</label>
              <input
                id="h-price"
                type="number"
                step="any"
                className="input w-full font-mono"
                placeholder="0.00"
                value={currentPrice}
                onChange={e => setCurrentPrice(e.target.value)}
              />
            </div>
            <div>
              <label className="block text-xs text-text-muted mb-1">Est. Value</label>
              <div className="input w-full font-mono text-text-secondary bg-surface-hover/50 pointer-events-none">
                {computedValue() > 0 ? fmtGBP(computedValue(), 2) : '—'}
              </div>
            </div>
            <div>
              <label className="block text-xs text-text-muted mb-1" htmlFor="h-currency">Currency</label>
              <select id="h-currency" className="input w-full" value={currency} onChange={e => setCurrency(e.target.value)}>
                <option value="GBP">GBP</option>
                <option value="USD">USD</option>
                <option value="EUR">EUR</option>
              </select>
            </div>
            <div className="col-span-2">
              <label className="block text-xs text-text-muted mb-1" htmlFor="h-notes">Notes</label>
              <input
                id="h-notes"
                className="input w-full"
                placeholder="Optional notes"
                value={notes}
                onChange={e => setNotes(e.target.value)}
              />
            </div>
          </div>
        )}

        {saveError && (
          <div className="text-xs text-rose-light border border-rose/40 bg-rose/10 rounded p-2" role="alert">
            {saveError}
          </div>
        )}

        <button
          type="submit"
          disabled={
            saving
            || !name.trim()
            || (isCashAccount ? !cashAmount : !quantity)
          }
          className="btn-primary w-full gap-2"
        >
          {saving ? <RefreshCw size={14} className="animate-spin" /> : <Plus size={14} />}
          {saving ? 'Adding...' : isCashAccount ? `Add £${cashAmount || '0'}` : 'Add Holding'}
        </button>
      </form>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// CSV Import Result Banner
// ─────────────────────────────────────────────────────────────────────────────

function ImportBanner({ result, onDismiss }: { result: CSVImportResponse; onDismiss: () => void }) {
  const isOk = result.status === 'success'
  return (
    <div className={clsx(
      'flex items-start gap-3 p-4 rounded-lg text-sm',
      isOk ? 'bg-emerald/10 text-emerald-light' : 'bg-amber/10 text-amber-light',
    )}>
      <div className="flex-1">
        <p className="font-medium">
          CSV Import: {result.rows_imported} row{result.rows_imported !== 1 ? 's' : ''} imported
          {result.rows_skipped > 0 && `, ${result.rows_skipped} skipped`}
        </p>
        {result.errors.length > 0 && (
          <ul className="mt-1 space-y-0.5 text-xs opacity-80">
            {result.errors.slice(0, 5).map((err, i) => <li key={i}>{err}</li>)}
            {result.errors.length > 5 && <li>...and {result.errors.length - 5} more</li>}
          </ul>
        )}
      </div>
      <button onClick={onDismiss} className="btn-ghost p-1" aria-label="Dismiss">
        <X size={14} />
      </button>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Holding audit history drawer — opens via the per-row History icon
// ─────────────────────────────────────────────────────────────────────────────

interface AuditEntry {
  id: number
  timestamp: string
  user: string
  action: string
  details: Record<string, any>
}

function HoldingAuditDrawer({ holdingId, holdingName, onClose }: {
  holdingId: number
  holdingName: string
  onClose: () => void
}) {
  const [entries, setEntries] = useState<AuditEntry[] | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    api.get('/api/audit', { params: { action: 'holding_*', holding_id: holdingId, limit: 50 } })
      .then(res => {
        setEntries(Array.isArray(res.data) ? res.data : [])
      })
      .catch(e => setError(e?.response?.data?.detail || e?.message || 'Failed to load history'))
  }, [holdingId])

  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
      onClick={e => { if (e.target === e.currentTarget) onClose() }}
    >
      <div className="bg-surface-card border border-border rounded-xl w-full max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-4 border-b border-border">
          <div>
            <h3 className="font-semibold text-text-primary">Audit history</h3>
            <p className="text-xs text-text-muted mt-0.5">{holdingName} · holding #{holdingId}</p>
          </div>
          <button type="button" onClick={onClose} className="btn-ghost p-1.5" aria-label="Close">
            <X size={14} />
          </button>
        </div>
        <div className="overflow-y-auto p-4">
          {error && (
            <div className="text-sm text-rose-light bg-rose/5 border border-rose/20 rounded-lg p-3">{error}</div>
          )}
          {!entries && !error && (
            <p className="text-sm text-text-muted">Loading…</p>
          )}
          {entries && entries.length === 0 && (
            <p className="text-sm text-text-muted">No audit entries for this holding yet. Future edits will show up here.</p>
          )}
          {entries && entries.length > 0 && (
            <ul className="space-y-2">
              {entries.map(e => {
                const ts = e.timestamp.replace('T', ' ').slice(0, 19)
                const op = e.action === 'holding_added' ? 'ADD' : e.action === 'holding_updated' ? 'EDIT' : e.action === 'holding_deleted' ? 'DEL' : e.action
                const opColor = op === 'ADD' ? 'text-emerald' : op === 'EDIT' ? 'text-blue' : op === 'DEL' ? 'text-rose-light' : 'text-text-muted'
                const changes = e.details?.changes as Record<string, { from: any; to: any }> | undefined
                return (
                  <li key={e.id} className="border border-border rounded-lg p-3 bg-surface">
                    <div className="flex items-center gap-2 text-xs">
                      <span className={clsx('font-mono font-semibold', opColor)}>{op}</span>
                      <span className="text-text-muted">·</span>
                      <span className="font-mono text-text-secondary">{ts}</span>
                      <span className="text-text-muted">·</span>
                      <span className="text-text-secondary">{e.user || '—'}</span>
                    </div>
                    {changes && Object.keys(changes).length > 0 && (
                      <ul className="mt-2 space-y-0.5 text-xs font-mono">
                        {Object.entries(changes).map(([field, diff]) => (
                          <li key={field} className="flex gap-2">
                            <span className="text-text-muted w-20">{field}:</span>
                            <span className="text-rose-light/70 line-through">{String(diff.from ?? '∅')}</span>
                            <span className="text-text-muted">→</span>
                            <span className="text-emerald">{String(diff.to ?? '∅')}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                    {op === 'ADD' && (
                      <p className="text-xs text-text-muted mt-1.5">
                        qty {String(e.details?.quantity ?? '?')} · value {String(e.details?.value ?? '?')}
                      </p>
                    )}
                    {op === 'DEL' && (
                      <p className="text-xs text-text-muted mt-1.5">
                        Was {String(e.details?.quantity ?? '?')} units of {e.details?.name ?? '?'}
                      </p>
                    )}
                  </li>
                )
              })}
            </ul>
          )}
        </div>
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Holding Row (inline editable)
// ─────────────────────────────────────────────────────────────────────────────

interface HoldingRowProps {
  holding: Holding
  accountId: number
  /** Parent account's type — used to treat un-classified rows on cash
   * accounts (Bank/Savings/Premium Bonds) as cash so the edit input renders. */
  accountType?: string
  /** Parent account's name — used to detect "Premium Bonds (Jessica)" etc. */
  accountName?: string
  onDeleted: () => void
}

function HoldingRow({ holding: h, accountId, accountType, accountName, onDeleted }: HoldingRowProps) {
  const canConfigure = useAuth(s => s.user?.permissions?.can_configure ?? true)
  const { updateHolding, deleteHolding } = useAccounts()
  const [editing, setEditing] = useState(false)
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [qty, setQty] = useState(String(h.quantity))
  const [avgCost, setAvgCost] = useState(h.avg_cost != null ? String(h.avg_cost) : '')
  const [valueStr, setValueStr] = useState(String(h.value))
  const [notes, setNotes] = useState(h.notes ?? '')
  const [targetWeight, setTargetWeight] = useState(h.target_weight_pct != null ? String(h.target_weight_pct) : '')
  const [auditOpen, setAuditOpen] = useState(false)
  // Non-blocking data-integrity warnings raised by the backend after a save
  // (e.g. a pence/pound 100x slip). Advisory only — the edit still applied.
  const [reconFlags, setReconFlags] = useState<ReconciliationFlag[]>([])

  // For cash-like holdings, the "value" field is the single source of truth
  // (quantity × 1 = value). Equities/funds compute value from qty × price.
  // Also treat a holding as cash-like if its account is a bank / savings /
  // Premium Bonds — that catches the manually-added rows that landed with
  // no asset_type and would otherwise show qty + cost inputs (useless for
  // cash) instead of a single value input.
  const accountTypeLower = (accountType || '').toLowerCase()
  const accountIsCash = ['bank', 'savings', 'premium bonds', 'cash', 'money market'].includes(accountTypeLower)
    || (accountName || '').toLowerCase().includes('premium bond')
  // Case-insensitive: rows stored as 'Cash' (old dropdown) must still get
  // the single-value cash editor, not the qty × price form.
  const isCashLike = isCashLikeAssetType(h.asset_type) || accountIsCash

  // avg_cost is £ (AJ Bell CSVs report Cost in sterling) while h.value is
  // native currency — always P&L against the GBP value or USD rows flip sign.
  const cost = h.avg_cost != null ? h.avg_cost * h.quantity : null
  const valueGbp = h.value_gbp ?? h.value
  const pnl = cost != null ? valueGbp - cost : null
  const pnlPct = cost != null && cost > 0 ? ((valueGbp - cost) / cost) * 100 : null

  const handleSave = async () => {
    setSaving(true)
    setSaveError(null)
    const patch: any = {
      notes: notes.trim() || null,
      target_weight_pct: targetWeight ? parseFloat(targetWeight) : null,
    }
    if (isCashLike) {
      // Direct value edit — bank balance, property value, mortgage outstanding
      const newValue = parseFloat(valueStr)
      if (!Number.isNaN(newValue)) {
        patch.value = newValue
        patch.quantity = newValue  // keep qty in lock-step for cash/liability
      }
    } else {
      // Number.isFinite, not `|| fallback` — editing a quantity to 0 must
      // save 0, not silently revert to the old quantity.
      patch.quantity = quantityPatchValue(qty, h.quantity)
      patch.avg_cost = avgCost ? parseFloat(avgCost) : null
    }
    const flags = await updateHolding(accountId, h.id, patch)
    setSaving(false)
    if (flags === null) {
      // Save FAILED — stay in edit mode so the user doesn't read the stale
      // row values back as "saved".
      setSaveError('Save failed — this edit was NOT applied. Fix the connection and press Save again, or Cancel to discard.')
      return
    }
    setReconFlags(flags)
    setEditing(false)
  }

  const handleCancel = () => {
    setQty(String(h.quantity))
    setAvgCost(h.avg_cost != null ? String(h.avg_cost) : '')
    setValueStr(String(h.value))
    setNotes(h.notes ?? '')
    setTargetWeight(h.target_weight_pct != null ? String(h.target_weight_pct) : '')
    setSaveError(null)
    setEditing(false)
  }

  const handleDelete = async () => {
    if (!window.confirm(`Delete "${h.name}"? This cannot be undone.`)) return
    await deleteHolding(accountId, h.id)
    onDeleted()
  }

  return (
    <tr
      className={clsx(
        'border-b border-border last:border-0 transition-colors group',
        editing ? 'bg-blue/5' : canConfigure ? 'hover:bg-surface-hover cursor-pointer' : '',
      )}
      onClick={() => { if (!editing && canConfigure) setEditing(true) }}
      title={editing || !canConfigure ? undefined : 'Click to edit'}
    >
      {/* Name + sector + staleness dot */}
      <td className="!py-2.5 !px-5">
        <div className="flex items-center gap-1.5">
          {h.updated_at && (() => {
            const ageH = (Date.now() - new Date(h.updated_at).getTime()) / 36e5
            // Skip the dot for rows that don't need pricing (cash, property, liability)
            if (isCashLike) return null
            const tone =
              ageH < 24  ? 'bg-emerald' :
              ageH < 48  ? 'bg-amber' :
              ageH < 168 ? 'bg-rose' :
              'bg-rose'
            const tip =
              ageH < 24  ? `Fresh — priced ${fmtAgo(h.updated_at)}` :
              ageH < 48  ? `Aging — priced ${fmtAgo(h.updated_at)}` :
              `Stale — priced ${fmtAgo(h.updated_at)}`
            return (
              <span
                className={clsx('inline-block w-1.5 h-1.5 rounded-full shrink-0', tone)}
                title={tip}
                aria-hidden
              />
            )
          })()}
          <p className="text-sm text-text-primary">{h.name}</p>
        </div>
        {h.sector && <p className="text-xs text-text-muted">{h.sector}</p>}
        {editing && notes !== undefined && (
          <input
            className="input w-full mt-1 text-xs"
            placeholder="Notes"
            value={notes}
            onChange={e => setNotes(e.target.value)}
            onClick={e => e.stopPropagation()}
          />
        )}
        {/* Hard save failure — the edit did NOT apply. Rendered while still
            in edit mode so the stale row values aren't mistaken for saved. */}
        {saveError && (
          <div
            className="mt-1.5 rounded border border-rose/40 bg-rose/10 px-2 py-1.5"
            onClick={e => e.stopPropagation()}
            role="alert"
          >
            <p className="text-xs text-rose-light leading-snug">{saveError}</p>
          </div>
        )}
        {/* Non-blocking data-integrity warning(s) after a suspicious save —
            e.g. a pence/pound 100x slip, a value zeroed or a sign flip. The
            edit DID apply; this only asks the user to confirm it was intended. */}
        {reconFlags.length > 0 && (
          <div
            className="mt-1.5 rounded border border-amber/40 bg-amber/10 px-2 py-1.5"
            onClick={e => e.stopPropagation()}
            role="alert"
          >
            <div className="flex items-start justify-between gap-2">
              <div className="space-y-0.5">
                {reconFlags.map((f, i) => (
                  <p key={i} className="text-xs text-amber leading-snug">
                    &#9888; {f?.message ?? 'This change looks unusual — please confirm'}
                  </p>
                ))}
              </div>
              <button
                type="button"
                className="text-xs text-text-muted hover:text-text-primary shrink-0"
                onClick={() => setReconFlags([])}
                aria-label="Dismiss warning"
              >
                &times;
              </button>
            </div>
          </div>
        )}
      </td>

      {/* Symbol — clickable to FT tearsheet (ISIN) or Yahoo (symbol) */}
      <td className="!py-2.5 !px-4 text-xs text-text-secondary font-mono hidden md:table-cell">
        {(() => {
          const sym = h.symbol
          const isin = h.isin
          if (!sym && !isin) return <span>-</span>
          // Crypto and US/LSE listings → Yahoo. UK OEICs (ISIN only) → FT.
          let href: string | null = null
          let label = sym ?? isin ?? '-'
          if (sym) {
            href = `https://finance.yahoo.com/quote/${encodeURIComponent(sym)}`
          } else if (isin) {
            href = `https://markets.ft.com/data/funds/tearsheet/summary?s=${encodeURIComponent(isin)}:GBP`
            label = isin
          }
          if (!href) return <span>{label}</span>
          return (
            <span className="inline-flex items-center gap-1.5">
              <a
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                onClick={e => e.stopPropagation()}
                title={isin ? `FT fund tearsheet for ${isin}` : `Yahoo Finance for ${sym}`}
                className="hover:text-blue-light hover:underline"
              >
                {label}
              </a>
              {sym && (
                <Link
                  to={`/app/chart?symbol=${encodeURIComponent(sym)}`}
                  onClick={e => e.stopPropagation()}
                  title="Open signal-aware chart (signals · SMC · trend)"
                  className="text-text-muted hover:text-blue-light"
                >
                  📈
                </Link>
              )}
            </span>
          )
        })()}
      </td>

      {/* Type */}
      <td className="!py-2.5 !px-4 hidden lg:table-cell">
        {h.asset_type && (
          <span className="badge badge-blue text-[10px]">
            {h.asset_type === 'bond' ? labelBondSector(h.sector) : labelAssetType(h.asset_type)}
          </span>
        )}
      </td>

      {/* Quantity */}
      <td className="!py-2.5 !px-4 text-sm text-text-primary text-right font-mono">
        {editing ? (
          <input
            type="number"
            step="any"
            className="input w-24 text-right font-mono text-sm ml-auto"
            value={qty}
            onChange={e => setQty(e.target.value)}
            onClick={e => e.stopPropagation()}
          />
        ) : (
          (h.quantity ?? 0).toLocaleString('en-GB', { maximumFractionDigits: 4 })
        )}
      </td>

      {/* Avg Cost */}
      <td className="!py-2.5 !px-4 text-sm text-text-secondary text-right font-mono hidden sm:table-cell">
        {editing ? (
          <div className="flex flex-col items-end gap-1">
            <input
              type="number"
              step="any"
              className="input w-24 text-right font-mono text-sm ml-auto"
              placeholder="0.00"
              value={avgCost}
              onChange={e => setAvgCost(e.target.value)}
              onClick={e => e.stopPropagation()}
            />
            <input
              type="number"
              step="0.5"
              min="0"
              max="100"
              className="input w-24 text-right font-mono text-sm ml-auto"
              placeholder="auto"
              value={targetWeight}
              onChange={e => setTargetWeight(e.target.value)}
              onClick={e => e.stopPropagation()}
              aria-label="Target %"
              title="Target weight % — intended portfolio allocation"
            />
          </div>
        ) : (
          fmtMoney(h.avg_cost, h.currency)
        )}
      </td>

      {/* Current Price */}
      <td className="!py-2.5 !px-4 text-sm text-text-secondary text-right font-mono hidden sm:table-cell">
        {fmtMoney(h.current_price, h.currency)}
      </td>

      {/* Value */}
      <td className="!py-2.5 !px-4 text-sm text-text-primary text-right font-bold">
        {editing && isCashLike ? (
          <div className="flex items-center justify-end gap-1.5">
            <input
              type="number"
              className="input w-32 text-right font-mono text-sm py-0.5"
              value={valueStr}
              onChange={e => setValueStr(e.target.value)}
              onClick={e => e.stopPropagation()}
              onKeyDown={e => {
                if (e.key === 'Enter') { e.preventDefault(); handleSave() }
                else if (e.key === 'Escape') { e.preventDefault(); handleCancel() }
              }}
              autoFocus
              aria-label="Value (GBP)"
            />
            <button
              type="button"
              onClick={e => { e.stopPropagation(); handleSave() }}
              disabled={saving}
              className="btn-primary text-xs px-2 py-1"
              title="Save (Enter)"
            >
              {saving ? '…' : 'Save'}
            </button>
            <button
              type="button"
              onClick={e => { e.stopPropagation(); handleCancel() }}
              className="btn-ghost text-xs px-2 py-1"
              title="Cancel (Esc)"
            >
              ✕
            </button>
          </div>
        ) : (
          <span
            className={clsx(
              'inline-flex items-center justify-end gap-1.5',
              canConfigure && isCashLike && 'cursor-pointer hover:text-blue-light group/edit',
            )}
            title={
              h.currency !== 'GBP'
                ? `${(h.value ?? 0).toLocaleString()} ${h.currency}`
                : canConfigure && isCashLike
                ? 'Click row to edit'
                : undefined
            }
            onClick={canConfigure && isCashLike ? (e => { e.stopPropagation(); setEditing(true) }) : undefined}
          >
            {fmtGBP(h.value_gbp ?? h.value)}
            {h.currency !== 'GBP' && (
              <span className="ml-1 text-[10px] text-text-muted font-mono">{h.currency}</span>
            )}
            {canConfigure && isCashLike && (
              <Pencil
                size={11}
                className="text-text-muted opacity-50 group-hover/edit:opacity-100 transition-opacity"
                aria-hidden
              />
            )}
          </span>
        )}
      </td>

      {/* P&L */}
      <td className="!py-2.5 !px-4 text-right hidden md:table-cell">
        {pnl != null ? (
          <div>
            <p className={clsx('text-sm font-mono font-medium', pnl >= 0 ? 'text-emerald' : 'text-rose-light')}>
              {fmtGBPSigned(pnl)}
            </p>
            {pnlPct != null && (
              <p className={clsx('text-xs font-mono', pnl >= 0 ? 'text-emerald' : 'text-rose-light')}>
                {fmtPctSigned(pnlPct, 1)}
              </p>
            )}
          </div>
        ) : (
          <span className="text-xs text-text-muted">—</span>
        )}
      </td>

      {/* Actions */}
      <td className="!py-2.5 !px-3 text-right" onClick={e => e.stopPropagation()}>
        {editing ? (
          <div className="flex items-center justify-end gap-1">
            <button
              onClick={handleSave}
              disabled={saving}
              className="btn-ghost p-1.5 text-emerald hover:bg-emerald/10"
              aria-label="Save changes"
              title="Save"
            >
              {saving ? <RefreshCw size={13} className="animate-spin" /> : <Check size={13} />}
            </button>
            <button
              onClick={handleCancel}
              className="btn-ghost p-1.5 text-text-muted"
              aria-label="Cancel edit"
              title="Cancel"
            >
              <X size={13} />
            </button>
          </div>
        ) : canConfigure ? (
          <div className="flex items-center justify-end gap-0.5">
            <button
              type="button"
              onClick={() => setAuditOpen(true)}
              className="btn-ghost p-1.5 text-text-muted hover:text-blue hover:bg-blue/10 opacity-0 group-hover:opacity-100 transition-opacity"
              aria-label={`History for ${h.name}`}
              title="Edit history"
            >
              <History size={13} />
            </button>
            <button
              type="button"
              onClick={handleDelete}
              className="btn-ghost p-1.5 text-text-muted hover:text-rose-light hover:bg-rose/10 opacity-0 group-hover:opacity-100 transition-opacity"
              aria-label={`Delete ${h.name}`}
              title="Delete holding"
            >
              <Trash2 size={13} />
            </button>
          </div>
        ) : null}
        {/* Drawer lives INSIDE the last <td> — a direct <tr> child that isn't
            td/th is invalid DOM and React re-parents it unpredictably. The
            drawer itself is position:fixed so visual output is unchanged. */}
        {auditOpen && (
          <HoldingAuditDrawer
            holdingId={h.id}
            holdingName={h.name}
            onClose={() => setAuditOpen(false)}
          />
        )}
      </td>
    </tr>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Holdings CSV column definition
// ─────────────────────────────────────────────────────────────────────────────

const HOLDING_COLUMNS: ColumnDef<Holding>[] = [
  { header: 'Name',          accessor: 'name' },
  { header: 'Symbol',        accessor: row => row.symbol ?? '' },
  { header: 'ISIN',          accessor: row => row.isin ?? '' },
  { header: 'Type',          accessor: row => row.asset_type ?? '' },
  { header: 'Quantity',      accessor: 'quantity' },
  { header: 'Avg Cost',      accessor: row => row.avg_cost ?? '' },
  { header: 'Current Price', accessor: row => row.current_price ?? '' },
  { header: 'Value',         accessor: 'value' },
  { header: 'Currency',      accessor: row => row.currency ?? 'GBP' },
  { header: 'Sector',        accessor: row => row.sector ?? '' },
  { header: 'Region',        accessor: row => row.region ?? '' },
  { header: 'Notes',         accessor: row => row.notes ?? '' },
]

// ─────────────────────────────────────────────────────────────────────────────
// Account Name Editor (inline)
// ─────────────────────────────────────────────────────────────────────────────

interface AccountNameEditorProps {
  account: Account
}

function AccountNameEditor({ account }: AccountNameEditorProps) {
  const { updateAccount } = useAccounts()
  const canConfigure = useAuth(s => s.user?.permissions?.can_configure ?? true)
  const [editing, setEditing] = useState(false)
  const [name, setName] = useState(account.name)
  const [owner, setOwner] = useState(account.owner)
  const [notes, setNotes] = useState(account.notes ?? '')
  const [accountType, setAccountType] = useState(account.account_type)
  const [discretionary, setDiscretionary] = useState(account.discretionary ?? false)
  const [saving, setSaving] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  const startEdit = (e: React.MouseEvent) => {
    e.stopPropagation()
    setEditing(true)
    setTimeout(() => inputRef.current?.focus(), 0)
  }

  const handleSave = async (e: React.MouseEvent | React.FormEvent) => {
    e.stopPropagation()
    if (e.type === 'submit') (e as React.FormEvent).preventDefault()
    if (!name.trim()) return
    setSaving(true)
    await updateAccount(account.id, {
      name: name.trim(),
      owner: owner.trim() || account.owner,
      account_type: accountType,
      discretionary,
      notes: notes.trim() || null,
    } as any)
    setSaving(false)
    setEditing(false)
  }

  const handleCancel = (e: React.MouseEvent) => {
    e.stopPropagation()
    setName(account.name)
    setOwner(account.owner)
    setNotes(account.notes ?? '')
    setAccountType(account.account_type)
    setDiscretionary(account.discretionary ?? false)
    setEditing(false)
  }

  if (!editing) {
    return (
      <div className="flex items-center gap-1.5 group/name min-w-0">
        <p className="text-sm font-semibold text-text-primary truncate">{account.name}</p>
        {canConfigure && (
          <button
            onClick={startEdit}
            className="btn-ghost p-0.5 opacity-0 group-hover/name:opacity-100 transition-opacity shrink-0"
            aria-label={`Edit ${account.name}`}
            title="Edit account name"
          >
            <Pencil size={11} className="text-text-muted" />
          </button>
        )}
      </div>
    )
  }

  return (
    <form
      onSubmit={handleSave}
      className="flex flex-col gap-1.5 min-w-0 w-full"
      onClick={e => e.stopPropagation()}
    >
      <div className="flex items-center gap-1 flex-wrap">
        <input
          ref={inputRef}
          className="input text-sm font-semibold py-0.5 px-2 h-7 flex-1 min-w-[140px]"
          value={name}
          onChange={e => setName(e.target.value)}
          required
          aria-label="Account name"
          placeholder="Account name"
        />
        <input
          className="input text-xs py-0.5 px-2 h-7 w-24"
          value={owner}
          onChange={e => setOwner(e.target.value)}
          placeholder="Owner"
          aria-label="Account owner"
        />
        <select
          className="input text-xs py-0.5 px-2 h-7 w-28"
          value={accountType}
          onChange={e => setAccountType(e.target.value)}
          aria-label="Account type"
        >
          {ACCOUNT_TYPES.map(t => (
            <option key={t} value={t}>{t}</option>
          ))}
        </select>
        <label className="flex items-center gap-1 text-[10px] text-text-secondary cursor-pointer select-none px-2 h-7 border border-border rounded bg-surface-card">
          <input
            type="checkbox"
            checked={discretionary}
            onChange={e => setDiscretionary(e.target.checked)}
            className="rounded border-border"
            aria-label="Discretionary — you can trade this account"
          />
          <span>Discretionary</span>
        </label>
        <button
          type="submit"
          disabled={saving || !name.trim()}
          className="btn-ghost p-1 text-emerald hover:bg-emerald/10"
          aria-label="Save account"
        >
          {saving ? <RefreshCw size={12} className="animate-spin" /> : <Save size={12} />}
        </button>
        <button
          type="button"
          onClick={handleCancel}
          className="btn-ghost p-1 text-text-muted"
          aria-label="Cancel editing account"
        >
          <X size={12} />
        </button>
      </div>
      <input
        className="input text-xs py-0.5 px-2 h-6"
        value={notes}
        onChange={e => setNotes(e.target.value)}
        placeholder="Notes (optional)"
        aria-label="Account notes"
      />
    </form>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Account Row (expandable)
// ─────────────────────────────────────────────────────────────────────────────

function AccountRow({ account, trend }: { account: Account; trend?: AccountTrend }) {
  const { holdings, fetchHoldings, fetchAccounts, fetchSummary, importCSV, deleteAccount } = useAccounts()
  const canConfigure = useAuth(s => s.user?.permissions?.can_configure ?? true)
  const [expanded, setExpanded] = useState(false)
  const [importResult, setImportResult] = useState<CSVImportResponse | null>(null)
  const [importError, setImportError] = useState<string | null>(null)
  const [importing, setImporting] = useState(false)
  const [importMerge, setImportMerge] = useState(false)
  const [showAddHolding, setShowAddHolding] = useState(false)
  const [syncing, setSyncing] = useState(false)
  // Inline outcome of per-account actions (Coinbase sync, Reprice) — these
  // used to fail silently, which reads as success.
  const [actionMsg, setActionMsg] = useState<{ ok: boolean; text: string } | null>(null)
  const fileRef = useRef<HTMLInputElement>(null)

  const accountHoldings = holdings[account.id] ?? []
  const ProviderIcon = PROVIDER_ICONS[account.provider] ?? Coins

  const handleExpand = () => {
    if (!expanded) fetchHoldings(account.id)
    setExpanded(!expanded)
  }

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    setImporting(true)
    setImportError(null)
    const result = await importCSV(account.id, file, { merge: importMerge })
    setImportResult(result)
    // null ⇒ the request itself failed (importCSV swallows into store.error).
    // Without this banner the import failed with NO feedback at all.
    if (result === null) {
      setImportError('CSV import FAILED — no rows were imported. Check the file and your connection, then try again.')
    }
    setImporting(false)
    if (fileRef.current) fileRef.current.value = ''
    if (!expanded) {
      setExpanded(true)
      fetchHoldings(account.id)
    }
  }

  const handleCoinbaseSync = async (e: React.MouseEvent) => {
    e.stopPropagation()
    setSyncing(true)
    setActionMsg(null)
    try {
      // Real endpoint is /api/connectors/coinbase/sync/{account_id} (operator
      // gated). The shared api client carries the auth header + error handling
      // — the old raw fetch to /api/coinbase/sync 404'd silently forever.
      const res = await api.post(`/api/connectors/coinbase/sync/${account.id}`)
      const synced = res.data?.synced ?? res.data?.holdings_count ?? null
      setActionMsg({
        ok: true,
        text: synced != null
          ? `Coinbase sync complete — ${synced} holding${synced === 1 ? '' : 's'} updated.`
          : 'Coinbase sync complete.',
      })
      await Promise.all([fetchHoldings(account.id), fetchAccounts(), fetchSummary()])
    } catch (err: any) {
      const detail = err?.response?.data?.detail ?? err?.message ?? String(err)
      setActionMsg({ ok: false, text: `Coinbase sync failed: ${detail}` })
    }
    setSyncing(false)
  }

  const totalPnl = accountHoldings.reduce((sum, h) => {
    const cost = h.avg_cost != null ? h.avg_cost * h.quantity : null
    return cost != null ? sum + ((h.value_gbp ?? h.value) - cost) : sum
  }, 0)
  const totalCost = accountHoldings.reduce((sum, h) => {
    const cost = h.avg_cost != null ? h.avg_cost * h.quantity : 0
    return sum + cost
  }, 0)
  const totalPnlPct = totalCost > 0 ? (totalPnl / totalCost) * 100 : null

  return (
    <>
      {showAddHolding && (
        <AddHoldingModal
          account={account}
          onClose={() => { setShowAddHolding(false); fetchHoldings(account.id) }}
        />
      )}

      <div className="card overflow-hidden">
        {/* Header */}
        <div
          className="flex items-center gap-3 px-5 py-4 cursor-pointer hover:bg-surface-hover transition-colors"
          onClick={handleExpand}
          role="button"
          tabIndex={0}
          aria-expanded={expanded}
          aria-label={`${account.name} - ${fmtGBP(account.total_value)}`}
          onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleExpand() } }}
        >
          <div className="w-9 h-9 rounded-lg bg-blue/10 border border-blue/20 flex items-center justify-center shrink-0">
            <ProviderIcon size={16} className="text-blue-light" aria-hidden />
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <AccountNameEditor account={account} />
              <span className="badge badge-blue text-[10px] shrink-0">{account.account_type}</span>
              {account.discretionary
                ? <span className="badge-green text-[10px] shrink-0">You can trade</span>
                : <span className="badge-neutral text-[10px] shrink-0">View only</span>
              }
            </div>
            <div className="flex items-center gap-3 text-xs text-text-muted mt-0.5">
              <span>{providerLabel(account.provider)}</span>
              <span>Owner: {account.owner}</span>
              {account.last_synced && <span>Synced: {new Date(account.last_synced).toLocaleDateString('en-GB')}</span>}
            </div>
          </div>

          <div className="text-right shrink-0">
            <div className="flex items-center justify-end gap-2">
              <p className="text-sm font-bold text-text-primary">{fmtGBP(account.total_value)}</p>
              {trend && trend.pnl_pct != null && Math.abs(trend.pnl_pct) >= 0.1 && (
                <TrendBadge pct={trend.pnl_pct} label="P&L" size="xs" />
              )}
            </div>
            {trend && Math.abs(trend.today_gbp) >= 1 ? (
              <p className={clsx(
                'text-xs font-mono',
                trend.today_gbp >= 0 ? 'text-emerald' : 'text-rose-light',
              )}>
                {fmtGBPSigned(trend.today_gbp)}
                {trend.today_pct != null && (
                  <span className="ml-1">
                    ({fmtPctSigned(trend.today_pct, 2)})
                  </span>
                )}
                <span className="text-text-muted ml-1">today</span>
              </p>
            ) : (
              <p className="text-xs text-text-muted">{account.holdings_count} holding{account.holdings_count !== 1 ? 's' : ''}</p>
            )}
          </div>

          <div className="shrink-0 ml-1">
            {expanded
              ? <ChevronDown size={16} className="text-text-muted" />
              : <ChevronRight size={16} className="text-text-muted" />}
          </div>
        </div>

        {/* Expanded: holdings + actions */}
        {expanded && (
          <div className="border-t border-border">

            {/* Account summary strip */}
            <div className="flex items-center gap-6 px-5 py-3 bg-surface-DEFAULT/50 border-b border-border text-xs flex-wrap">
              <div>
                <span className="text-text-muted">Total Value </span>
                <span className="font-bold text-text-primary">{fmtGBP(account.total_value)}</span>
              </div>
              <div>
                <span className="text-text-muted">Holdings </span>
                <span className="font-bold text-text-primary">{accountHoldings.length}</span>
              </div>
              {totalPnlPct != null && (
                <div>
                  <span className="text-text-muted">P&amp;L </span>
                  <span className={clsx('font-bold', totalPnl >= 0 ? 'text-emerald' : 'text-rose-light')}>
                    {fmtGBPSigned(totalPnl)}
                    {' '}({fmtPctSigned(totalPnlPct, 1)})
                  </span>
                </div>
              )}
              {account.last_synced && (
                <div>
                  <span className="text-text-muted">Last synced </span>
                  <span className="text-text-secondary">{new Date(account.last_synced).toLocaleDateString('en-GB')}</span>
                </div>
              )}
              <div className="flex items-center gap-1 ml-auto">
                {account.discretionary
                  ? <span className="badge-green text-[10px]">You can trade</span>
                  : <span className="badge-neutral text-[10px]">View only</span>
                }
              </div>
            </div>

            {/* Action bar (hidden for read-only viewers) */}
            {canConfigure && (
              <div className="flex items-center gap-2 px-5 py-3 bg-surface-DEFAULT flex-wrap">
                <button
                  type="button"
                  onClick={e => { e.stopPropagation(); setShowAddHolding(true) }}
                  className="btn-secondary text-xs gap-1.5"
                  aria-label="Add holding"
                >
                  <Plus size={13} aria-hidden />
                  Add Holding
                </button>

                <label className="btn-secondary text-xs gap-1.5 cursor-pointer"
                       title="ISINs auto-resolve via curated map → yfinance → OpenFIGI">
                  <Upload size={13} aria-hidden />
                  {importing ? 'Importing...' : 'Import CSV'}
                  <input
                    ref={fileRef}
                    type="file"
                    accept=".csv"
                    className="sr-only"
                    onChange={handleFile}
                    disabled={importing}
                  />
                </label>

                <label
                  className="flex items-center gap-1.5 text-[10px] text-text-muted cursor-pointer select-none"
                  title="Merge: upsert by ISIN/symbol/name instead of wiping the account first"
                >
                  <input
                    type="checkbox"
                    checked={importMerge}
                    onChange={(e) => setImportMerge(e.target.checked)}
                    className="rounded border-border bg-surface-card"
                  />
                  Merge
                </label>

                {account.provider === 'coinbase' && (
                  <button
                    type="button"
                    onClick={handleCoinbaseSync}
                    disabled={syncing}
                    className="btn-secondary text-xs gap-1.5"
                    aria-label="Sync Coinbase"
                  >
                    <RefreshCw size={13} className={syncing ? 'animate-spin' : ''} aria-hidden />
                    <span className="hidden sm:inline">{syncing ? 'Syncing...' : 'Sync'}</span>
                  </button>
                )}

                <button
                  type="button"
                  onClick={async (e) => {
                    e.stopPropagation()
                    setActionMsg(null)
                    try {
                      const res = await api.post(`/api/accounts/${account.id}/refresh-prices`)
                      const body = res.data ?? {}
                      const repriced = body.repriced ?? null
                      const delta = Number(body.delta_gbp ?? 0)
                      setActionMsg({
                        ok: true,
                        text: repriced != null
                          ? `Repriced ${repriced} holding${repriced === 1 ? '' : 's'} · Δ ${delta >= 0 ? '+' : '-'}£${Math.abs(delta).toLocaleString()}.`
                          : 'Reprice complete.',
                      })
                      await fetchHoldings(account.id)
                      fetchAccounts()
                      fetchSummary()
                    } catch (err: any) {
                      // Was a silent swallow — a dead reprice looked identical to success
                      const detail = err?.response?.data?.detail ?? err?.message ?? String(err)
                      setActionMsg({ ok: false, text: `Reprice failed: ${detail}` })
                    }
                  }}
                  className="btn-secondary text-xs gap-1.5"
                  aria-label={`Refresh prices for ${account.name}`}
                  title="Reprice this account's holdings now"
                >
                  <RefreshCw size={13} aria-hidden />
                  <span className="hidden sm:inline">Reprice</span>
                </button>

                {/* Cash-account fix buttons — visible only on Bank/Savings/Premium-Bonds
                    accounts. Hit the normalise/scrub endpoints and refresh holdings so
                    the £2.5B-style "typed same number into qty AND price" bug clears
                    in one click. Tucked behind an "Advanced" collapse so household
                    members never see engineering-speak repair tools by default. */}
                {(() => {
                  const t = (account.account_type || '').toLowerCase()
                  const n = (account.name || '').toLowerCase()
                  const isCashAcc = ['bank', 'savings', 'premium bonds', 'cash', 'money market'].includes(t)
                    || n.includes('premium bond')
                  if (!isCashAcc) return null
                  return (
                    <details className="min-w-0">
                      <summary
                        className="btn-ghost text-xs text-text-muted cursor-pointer select-none inline-flex items-center gap-1"
                        title="Data-repair tools — only needed if this account's numbers look wrong"
                      >
                        Advanced
                      </summary>
                      <div className="flex items-center gap-2 mt-2 flex-wrap">
                      <button
                        type="button"
                        onClick={async (e) => {
                          e.stopPropagation()
                          try {
                            const res = await fetch('/api/accounts/normalise-cash-holdings', {
                              method: 'POST',
                              headers: { Authorization: `Bearer ${localStorage.getItem('av-access-token') || ''}` },
                            })
                            if (res.ok) {
                              const body = await res.json()
                              // Refetch all three so the header total + account
                              // total + holdings rows all reflect the fix.
                              // Holdings alone leaves the £2.5B account header stale.
                              await Promise.all([
                                fetchHoldings(account.id),
                                fetchAccounts(),
                                fetchSummary(),
                              ])
                              const n = body.fixed_count ?? 0
                              if (n > 0) {
                                alert(`Fixed ${n} cash holding${n !== 1 ? 's' : ''}.`)
                              } else {
                                alert('All cash holdings already correct — nothing to fix.')
                              }
                            } else {
                              alert(`Fix failed: ${res.status} ${res.statusText}`)
                            }
                          } catch (err) {
                            alert(`Fix failed: ${err}`)
                          }
                        }}
                        className="btn-secondary text-xs gap-1.5"
                        aria-label="Fix cash holdings — restore qty=value, price=£1"
                        title="Normalise cash holdings: collapse qty×price bugs, set asset_type=cash. One-click fix."
                      >
                        <RefreshCw size={13} aria-hidden />
                        <span className="hidden sm:inline">Fix Cash</span>
                      </button>
                      <button
                        type="button"
                        onClick={async (e) => {
                          e.stopPropagation()
                          const authHeader = {
                            Authorization: `Bearer ${localStorage.getItem('av-access-token') || ''}`,
                          }
                          try {
                            // Two-step fix: scrub outliers first, then backfill
                            // any resulting gaps so the 30d sparkline becomes
                            // a clean continuous line.
                            // Step 1: kill outliers (the £2.5B hump)
                            const scrubRes = await fetch('/api/accounts/scrub-nav-outliers', {
                              method: 'POST', headers: authHeader,
                            })
                            const scrubBody = scrubRes.ok ? await scrubRes.json() : null

                            // Step 2: reconstruct 30 days of REAL historical NAV
                            // by fetching each holding's actual closing price
                            // for each trading day and summing. Persisted to
                            // Postgres daily_snapshots so it survives restarts.
                            const reconRes = await fetch('/api/accounts/reconstruct-nav-history?days=30', {
                              method: 'POST', headers: authHeader,
                            })
                            const reconBody = reconRes.ok ? await reconRes.json() : null

                            await Promise.all([fetchAccounts(), fetchSummary()])

                            const deleted = scrubBody?.deleted_count ?? 0
                            const rebuilt = reconBody?.reconstructed_count ?? 0
                            const symbols = reconBody?.symbols_with_history ?? 0
                            const attempted = reconBody?.symbols_attempted ?? 0
                            alert(
                              `Chart rebuilt with REAL prices.\n\n`
                              + `Removed ${deleted} outlier snapshot${deleted !== 1 ? 's' : ''}.\n`
                              + `Reconstructed ${rebuilt} day${rebuilt !== 1 ? 's' : ''} from ${symbols}/${attempted} symbols' historical closes.\n\n`
                              + `History is persisted to the database — survives restarts. Today's row auto-updates on every page load.`
                            )
                          } catch (err) {
                            alert(`Fix Chart failed: ${err}`)
                          }
                        }}
                        className="btn-secondary text-xs gap-1.5"
                        aria-label="Scrub NAV outliers + backfill chart gaps"
                        title="Two-step chart cleanup: (1) delete daily snapshots >2× off neighbours, (2) fill any missing days at current NAV."
                      >
                        <RefreshCw size={13} aria-hidden />
                        <span className="hidden sm:inline">Fix Chart</span>
                      </button>
                      </div>
                    </details>
                  )
                })()}

                <button
                  type="button"
                  onClick={e => { e.stopPropagation(); if (window.confirm('Deactivate this account?')) deleteAccount(account.id) }}
                  className="btn-ghost text-xs text-rose-light hover:bg-rose/10 gap-1.5 ml-auto"
                  aria-label={`Delete ${account.name}`}
                >
                  <Trash2 size={13} aria-hidden />
                  Remove Account
                </button>
              </div>
            )}

            {/* Per-account action outcome (Coinbase sync / Reprice) */}
            {actionMsg && (
              <div className="px-5 pt-3">
                <div
                  className={clsx(
                    'flex items-start gap-3 p-3 rounded-lg text-sm',
                    actionMsg.ok ? 'bg-emerald/10 text-emerald' : 'bg-rose/10 text-rose-light',
                  )}
                  role={actionMsg.ok ? 'status' : 'alert'}
                >
                  <p className="flex-1">{actionMsg.text}</p>
                  <button
                    type="button"
                    onClick={() => setActionMsg(null)}
                    className="btn-ghost p-1"
                    aria-label="Dismiss"
                  >
                    <X size={14} />
                  </button>
                </div>
              </div>
            )}

            {/* Import failure — the request itself died (network / 4xx / 5xx) */}
            {importError && (
              <div className="px-5 pt-3">
                <div
                  className="flex items-start gap-3 p-4 rounded-lg text-sm bg-rose/10 text-rose-light"
                  role="alert"
                >
                  <p className="flex-1 font-medium">{importError}</p>
                  <button
                    type="button"
                    onClick={() => setImportError(null)}
                    className="btn-ghost p-1"
                    aria-label="Dismiss"
                  >
                    <X size={14} />
                  </button>
                </div>
              </div>
            )}

            {/* Import result */}
            {importResult && (
              <div className="px-5 pb-3 pt-3">
                <ImportBanner result={importResult} onDismiss={() => setImportResult(null)} />
              </div>
            )}

            {/* Holdings table */}
            {accountHoldings.length > 0 ? (
              <div>
                <div className="flex justify-end px-5 py-2 border-b border-border">
                  <ExportCSV
                    data={accountHoldings}
                    columns={HOLDING_COLUMNS}
                    filename={`${account.name.toLowerCase().replace(/\s+/g, '-')}-holdings`}
                  />
                </div>
              <div className="overflow-x-auto table-responsive">
                <table className="table-pro w-full" aria-label={`Holdings for ${account.name}`}>
                  <thead>
                    <tr>
                      <th className="py-2 !px-5">Name</th>
                      <th className="py-2 !px-4 hidden md:table-cell">Symbol</th>
                      <th className="py-2 !px-4 hidden lg:table-cell">Type</th>
                      <th className="py-2 !px-4 !text-right">Qty</th>
                      <th className="py-2 !px-4 !text-right hidden sm:table-cell">Avg Cost</th>
                      <th className="py-2 !px-4 !text-right hidden sm:table-cell">Price</th>
                      <th className="py-2 !px-4 !text-right">Value</th>
                      <th className="py-2 !px-4 !text-right hidden md:table-cell">P&amp;L</th>
                      <th className="py-2 !px-3 !text-right w-16">
                        <span className="sr-only">Actions</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {accountHoldings.map(h => (
                      <HoldingRow
                        key={h.id}
                        holding={h}
                        accountId={account.id}
                        accountType={account.account_type}
                        accountName={account.name}
                        onDeleted={() => {}}
                      />
                    ))}
                  </tbody>
                  {accountHoldings.length > 1 && (
                    <tfoot>
                      {/* Cells mirror the header/body columns' responsive
                          hidden classes exactly — a colSpan that assumed all
                          columns visible misaligned the totals on mobile,
                          where Symbol/Type/Avg Cost/Price/P&L are hidden. */}
                      <tr className="border-t border-border bg-surface-DEFAULT/40">
                        <td className="py-2 px-5 text-xs text-text-muted font-medium">
                          {accountHoldings.length} holdings
                        </td>
                        <td className="hidden md:table-cell" />
                        <td className="hidden lg:table-cell" />
                        <td />
                        <td className="hidden sm:table-cell" />
                        <td className="hidden sm:table-cell" />
                        <td className="py-2 px-4 text-right text-sm font-bold text-text-primary">
                          {/* Sum in GBP — value_gbp is FX-converted server-side */}
                          {fmtGBP(accountHoldings.reduce((s, h) => s + (h.value_gbp ?? h.value), 0))}
                        </td>
                        <td className={clsx(
                          'py-2 px-4 text-right text-sm font-bold hidden md:table-cell',
                          totalPnl >= 0 ? 'text-emerald' : 'text-rose-light',
                        )}>
                          {totalPnlPct != null
                            ? `${fmtGBPSigned(totalPnl)} (${fmtPctSigned(totalPnlPct, 1)})`
                            : '—'
                          }
                        </td>
                        <td />
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
              </div>
            ) : (
              <div className="px-5 py-8 text-center text-sm text-text-muted">
                No holdings yet.{' '}
                <button
                  onClick={() => setShowAddHolding(true)}
                  className="text-blue-light hover:underline"
                >
                  Add one manually
                </button>
                {' '}or import a CSV.
              </div>
            )}
          </div>
        )}
      </div>
    </>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Main Page
// ─────────────────────────────────────────────────────────────────────────────

export default function AccountsPage() {
  const {
    accounts, summary, loading, error: accountsError,
    fetchAccounts, fetchSummary, holdings, fetchHoldings, clearError,
  } = useAccounts()
  const user = useAuth(s => s.user)
  const canConfigure = user?.permissions?.can_configure ?? true
  const [showAdd, setShowAdd] = useState(false)
  const [ajbImporting, setAjbImporting] = useState(false)
  const [ajbResult, setAjbResult] = useState<{
    message: string
    ok: boolean
    reconciliation?: Array<{
      account_id?: number
      account_name?: string | null
      category?: string
      severity?: string
      message?: string
    }> | null
  } | null>(null)
  const [ajbTargetAccount, setAjbTargetAccount] = useState<string>('auto')
  const [showIgPaste, setShowIgPaste] = useState(false)
  const ajbFileRef = useRef<HTMLInputElement>(null)
  const [trendPct30d, setTrendPct30d] = useState<number | null>(null)
  const [accountTrends, setAccountTrends] = useState<Record<string, AccountTrend>>({})
  const [repricing, setRepricing] = useState(false)
  const [repriceMsg, setRepriceMsg] = useState<string | null>(null)
  const [stuckInfo, setStuckInfo] = useState<{ count: number; total_value_gbp: number } | null>(null)
  const [showStuckList, setShowStuckList] = useState(false)
  const [stuckHoldings, setStuckHoldings] = useState<Array<{
    holding_id: number; account_name: string; name: string; value_gbp: number
  }>>([])
  const [backfilling, setBackfilling] = useState(false)
  const [showIdEditor, setShowIdEditor] = useState(false)
  const [repriceStatus, setRepriceStatus] = useState<{
    ok: boolean; ran_at: string | null; repriced?: number; delta_gbp?: number
  } | null>(null)

  const fetchRepriceStatus = async () => {
    try {
      const res = await fetch('/api/accounts/reprice-status', {
        headers: { Authorization: `Bearer ${localStorage.getItem('av-access-token') || ''}` },
      })
      if (!res.ok) return
      setRepriceStatus(await res.json())
    } catch { /* silent */ }
  }

  const refetchStuck = async () => {
    try {
      const res = await fetch('/api/accounts/stuck-holdings', {
        headers: { Authorization: `Bearer ${localStorage.getItem('av-access-token') || ''}` },
      })
      if (!res.ok) return
      const body = await res.json()
      setStuckInfo({ count: body.count, total_value_gbp: body.total_value_gbp })
      setStuckHoldings(body.holdings ?? [])
    } catch { /* silent */ }
  }

  const handleFixCurrencies = async () => {
    setRepriceMsg(null)
    try {
      const res = await fetch('/api/accounts/fix-currencies', {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('av-access-token') || ''}` },
      })
      if (!res.ok) {
        const detail = await res.json().catch(() => ({}))
        setRepriceMsg(`Fix-currencies failed: ${detail?.detail ?? res.statusText}`)
        return
      }
      const body = await res.json()
      if (body.changed_count === 0) {
        setRepriceMsg('No currency corrections needed — every holding looks right.')
      } else {
        setRepriceMsg(
          `Fixed ${body.changed_count} currency tags. ` +
          `Click "Refresh prices" to re-mark with the corrected basis.`
        )
      }
      fetchAccounts()
      fetchSummary()
    } catch (e: any) {
      setRepriceMsg(`Fix-currencies error: ${e?.message ?? String(e)}`)
    } finally {
      setTimeout(() => setRepriceMsg(null), 10000)
    }
  }

  const handleBackfillIsins = async () => {
    setBackfilling(true)
    setRepriceMsg(null)
    try {
      const res = await fetch('/api/accounts/backfill-isins', {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('av-access-token') || ''}` },
      })
      if (!res.ok) {
        const detail = await res.json().catch(() => ({}))
        setRepriceMsg(`Backfill failed: ${detail?.detail ?? res.statusText}`)
        return
      }
      const body = await res.json()
      const rp = body.reprice
      let backMsg = `Backfilled ${body.backfilled} ISINs from the curated UK fund map. `
      if (rp?.repriced != null) {
        const delta = Number(rp.delta_gbp ?? 0)
        const sign = delta >= 0 ? '+' : ''
        backMsg += `Reprice: ${rp.repriced} updated, Δ ${sign}£${Math.abs(delta).toLocaleString()}.`
      } else {
        backMsg += 'Click "Refresh prices" now to mark them to market.'
      }
      setRepriceMsg(backMsg)
      await refetchStuck()
      fetchAccounts()
      fetchSummary()
      fetchRepriceStatus()
    } catch (e: any) {
      setRepriceMsg(`Backfill error: ${e?.message ?? String(e)}`)
    } finally {
      setBackfilling(false)
      setTimeout(() => setRepriceMsg(null), 10000)
    }
  }

  // Fetch stuck-holdings summary once on mount — surfaces the "£2M of
  // SIPP funds have no ISIN, they're all frozen" data-quality gap.
  useEffect(() => {
    refetchStuck()
    fetchRepriceStatus()
    // Poll status every 60s so the "last repriced Xm ago" badge stays fresh
    const id = setInterval(fetchRepriceStatus, 60_000)
    return () => clearInterval(id)
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const handleRefreshPrices = async () => {
    setRepricing(true)
    setRepriceMsg(null)
    try {
      const res = await fetch('/api/accounts/refresh-prices', {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('av-access-token') || ''}` },
      })
      if (!res.ok) {
        const detail = await res.json().catch(() => ({}))
        setRepriceMsg(`Refresh failed: ${detail?.detail ?? res.statusText}`)
        return
      }
      const body = await res.json()
      setRepriceMsg(
        `Repriced ${body.repriced}/${body.total_holdings}. ` +
        `Δ ${body.delta_gbp >= 0 ? '+' : ''}£${Math.abs(body.delta_gbp).toLocaleString()}. ` +
        `Persisted ${body.persisted_to_db ?? 0} to DB.`
      )
      fetchAccounts()
      fetchSummary()
      fetchRepriceStatus()
    } catch (e: any) {
      setRepriceMsg(`Refresh error: ${e?.message ?? String(e)}`)
    } finally {
      setRepricing(false)
      setTimeout(() => setRepriceMsg(null), 8000)
    }
  }

  useEffect(() => {
    fetchAccounts()
    fetchSummary()
  }, []) // eslint-disable-line

  // 30-day net-worth trend for the Total Net Worth KPI card
  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const res = await fetch('/api/reports/performance-series?days=30', {
          headers: { Authorization: `Bearer ${localStorage.getItem('av-access-token') || ''}` },
        })
        if (!res.ok || cancelled) return
        const body = await res.json()
        const rows = Array.isArray(body) ? body : (body?.series ?? [])
        const vals = rows
          .filter((r: any) => r && r.portfolio_value != null)
          .map((r: any) => Number(r.portfolio_value))
        if (vals.length >= 2) {
          const first = vals[0]
          const last = vals[vals.length - 1]
          if (first > 0 && !cancelled) {
            setTrendPct30d(((last - first) / first) * 100)
          }
        }
      } catch { /* silent */ }
    })()
    return () => { cancelled = true }
  }, [])

  // Per-account trend snapshot (today's change + P&L%) from /api/pnl/live.
  // NOTE: the live P&L router mounts at prefix /api/pnl (backend/api/live_pnl.py)
  // — the old '/api/live-pnl/live' path 404'd silently and every per-account
  // today-change badge was permanently dead. Shared api client carries auth.
  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const res = await api.get<LivePnlResponse>('/api/pnl/live')
        if (cancelled) return
        const body: LivePnlResponse = res.data
        const map: Record<string, AccountTrend> = {}
        // Aggregate today's change per account from positions
        for (const p of body.positions ?? []) {
          const key = p.account
          if (!key) continue
          if (!map[key]) map[key] = { today_gbp: 0, today_pct: null, pnl_pct: null }
          map[key].today_gbp += Number(p.today_change_gbp ?? 0)
        }
        // Attach pnl_pct and compute today_pct from per-account value
        for (const a of body.by_account ?? []) {
          const key = a.account
          if (!key) continue
          if (!map[key]) map[key] = { today_gbp: 0, today_pct: null, pnl_pct: null }
          map[key].pnl_pct = a.pnl_pct ?? null
          const baseline = (a.value ?? 0) - map[key].today_gbp
          map[key].today_pct = baseline > 0 ? (map[key].today_gbp / baseline) * 100 : null
        }
        if (!cancelled) setAccountTrends(map)
      } catch { /* silent */ }
    })()
    return () => { cancelled = true }
  }, [])

  const handleAjBellUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    setAjbImporting(true)
    setAjbResult(null)
    try {
      const fd = new FormData()
      fd.append('file', file)
      const qs = ajbTargetAccount !== 'auto'
        ? `?target_account_id=${encodeURIComponent(ajbTargetAccount)}`
        : ''
      const res = await fetch(`/api/accounts/import-aj-bell${qs}`, {
        method: 'POST',
        body: fd,
        headers: { Authorization: `Bearer ${localStorage.getItem('av-access-token') || ''}` },
      })
      if (!res.ok) {
        const err = await res.text()
        setAjbResult({ message: `Import failed: ${err.slice(0, 200)}`, ok: false })
      } else {
        const data = await res.json()
        const sections = Object.keys(data.sections_imported || {}).length
        const recon = Array.isArray(data.reconciliation) ? data.reconciliation : null
        setAjbResult({
          message: `Imported ${sections} section${sections !== 1 ? 's' : ''} from ${data.filename}`,
          ok: true,
          reconciliation: recon && recon.length ? recon : null,
        })
        await fetchAccounts()
        await fetchSummary()
      }
    } catch (err) {
      setAjbResult({ message: `Network error: ${err}`, ok: false })
    } finally {
      setAjbImporting(false)
      if (ajbFileRef.current) ajbFileRef.current.value = ''
    }
  }

  const pieData = (summary?.by_provider ?? []).map((p, i) => ({
    name: providerLabel(p.provider),
    value: p.total_value,
    color: COLORS[i % COLORS.length],
  })) ?? []

  return (
    <div className="space-y-6 animate-fade-in">
      {showAdd && <AddAccountModal onClose={() => setShowAdd(false)} />}

      <PageHeader
        title="Accounts"
        subtitle={canConfigure ? 'Trading accounts across all brokers' : 'Read-only view'}
        actions={canConfigure && (
          <>
            <select
              value={ajbTargetAccount}
              onChange={(e) => setAjbTargetAccount(e.target.value)}
              className="px-2 py-1.5 rounded-lg border border-border bg-surface-card text-xs text-text-primary focus:outline-none focus:ring-1 focus:ring-blue"
              title="Route all sections to this account. Pick 'auto' for Simon's CSV (ISA/SIPP split); explicit when importing Jessica / Unman / anything else."
              aria-label="Target account for AJ Bell import"
            >
              <option value="auto">auto (Simon ISA+SIPP)</option>
              {accounts
                .filter(a => a.provider === 'aj_bell')
                .map(a => (
                  <option key={a.id} value={a.id}>
                    → {a.name}
                  </option>
                ))}
            </select>
            <label className="btn-secondary gap-2 cursor-pointer" title="Upload an AJ Bell CSV. Use 'auto' for Simon's CSV; pick a specific account for Jessica, Unman, etc.">
              <Upload size={14} aria-hidden />
              <span className="hidden sm:inline">
                {ajbImporting ? 'Importing AJ Bell…' : 'Sync AJ Bell CSV'}
              </span>
              <span className="sm:hidden" aria-label="Sync AJ Bell CSV">
                {ajbImporting ? '…' : 'AJB'}
              </span>
              <input
                ref={ajbFileRef}
                type="file"
                accept=".csv"
                className="sr-only"
                onChange={handleAjBellUpload}
                disabled={ajbImporting}
              />
            </label>
            <button
              type="button"
              onClick={handleRefreshPrices}
              disabled={repricing}
              className="btn-secondary gap-2"
              title={
                repriceStatus?.ran_at
                  ? `Force mark-to-market every holding now. Last run: ${fmtAgo(repriceStatus.ran_at)}${
                      typeof repriceStatus.repriced === 'number'
                        ? ` (${repriceStatus.repriced} updated)`
                        : ''
                    }.`
                  : 'Force mark-to-market every holding now (same pipeline the celery beat runs every 15 min).'
              }
              aria-label="Refresh prices"
            >
              <RefreshCw size={14} className={repricing ? 'animate-spin' : ''} aria-hidden />
              <span className="hidden sm:inline">
                {repricing ? 'Repricing…' : 'Refresh prices'}
              </span>
              <span className="sm:hidden">{repricing ? '…' : 'Refresh'}</span>
              {/* Inline freshness pill — uses tabular numbers and dims when stale.
                  Replaces the stacked-under-button subtext that broke action-bar
                  baseline alignment. */}
              {repriceStatus?.ran_at && (() => {
                const ageMin = (Date.now() - new Date(repriceStatus.ran_at).getTime()) / 60000
                const tone = ageMin < 30
                  ? 'text-emerald-300'
                  : ageMin < 360
                  ? 'text-amber-300'
                  : 'text-rose-300'
                return (
                  <span className={`hidden sm:inline text-[10px] font-mono ${tone}`} aria-hidden>
                    · {fmtAgo(repriceStatus.ran_at)}
                  </span>
                )
              })()}
            </button>
            <button
              type="button"
              onClick={() => setShowIgPaste(true)}
              className="btn-secondary gap-2"
              title="Paste holdings copied from the IG mobile app (tap-hold a position → Copy all). Works for any account."
              aria-label="Paste IG positions"
            >
              <Upload size={14} aria-hidden />
              <span className="hidden sm:inline">Paste IG positions</span>
              <span className="sm:hidden">IG</span>
            </button>
            <button type="button" onClick={() => setShowAdd(true)} className="btn-primary gap-2" aria-label="Add a new account">
              <Plus size={14} aria-hidden />
              <span className="hidden sm:inline">Add Account</span>
              <span className="sm:hidden">Add</span>
            </button>
          </>
        )}
      />
      {showIgPaste && (
        <IgPasteModal
          accounts={accounts}
          onClose={() => {
            setShowIgPaste(false)
            fetchAccounts()
            fetchSummary()
            // Also refresh any holdings lists already loaded (i.e. accounts
            // the user has expanded) so pasted IG positions appear without
            // collapsing and re-expanding the account.
            Object.keys(holdings).forEach(id => fetchHoldings(Number(id)))
          }}
        />
      )}

      {/* Store-level failure surface — mutations record errors here; without
          this they were invisible (modals closed as if saved). */}
      {accountsError && (
        <div className="card p-3 border-rose/40 bg-rose/5 text-sm flex items-start gap-2" role="alert">
          <span className="text-rose-light font-semibold shrink-0">Last action failed:</span>
          <span className="text-text-primary flex-1 break-words">{accountsError}</span>
          <button
            type="button"
            onClick={clearError}
            className="text-text-muted hover:text-text-primary text-xs shrink-0"
            aria-label="Dismiss error"
          >
            ✕
          </button>
        </div>
      )}

      {/* Bulk symbol / ISIN editor — opened from the amber "stuck holdings" banner */}
      <IdentifierEditor
        open={showIdEditor}
        onClose={() => setShowIdEditor(false)}
        onSaved={() => { refetchStuck(); fetchAccounts(); fetchSummary() }}
      />

      {/* Performance pills — Today / 7d / 30d NAV deltas */}
      <PerformanceStrip />

      {/* Data quality health — compact always-visible status line */}
      <DataQualityPanel />

      {/* Pricing review — reprice-guard flags needing a human decision.
          Renders nothing when there's nothing flagged. */}
      <PricingReviewCard />

      {/* Reprice feedback — auto-dismisses after 8s */}
      {repriceMsg && (
        <div className="card p-3 border-blue/40 bg-blue/5 text-sm text-text-primary" role="status">
          {repriceMsg}
        </div>
      )}

      {/* Stuck-holdings alert — holdings with no symbol/ISIN can't be priced */}
      {stuckInfo && stuckInfo.count > 0 && (
        <div className="card p-3 border-amber/40 bg-amber/5 text-sm">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="text-text-primary">
              <span className="font-semibold text-amber-light">
                {stuckInfo.count} holdings can't be priced automatically
              </span>
              {' '}— £{(stuckInfo.total_value_gbp ?? 0).toLocaleString()} frozen at seed value
              because they're missing both a symbol and an ISIN. Add an ISIN
              to any fund and the reprice task will pick it up via the FT UK fund pricer.
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              <button
                type="button"
                onClick={() => setShowIdEditor(true)}
                className="btn-secondary text-xs px-3 py-1"
                title="Open a bulk editor — paste tickers or ISINs for any number of holdings and save in one click."
              >
                Edit tickers / ISINs
              </button>
              <button
                type="button"
                onClick={() => setShowStuckList(s => !s)}
                className="text-amber-light hover:text-amber underline text-xs"
              >
                {showStuckList ? 'Hide list' : 'Show list'}
              </button>
              {/* Data-repair tools with engineering-speak labels live behind an
                  "Advanced" collapse so household members never see them by
                  default. Functionality unchanged. */}
              <details className="min-w-0">
                <summary
                  className="text-xs text-text-muted hover:text-text-secondary cursor-pointer select-none inline-flex items-center gap-1"
                  title="Data-repair tools — only needed if the automatic fixes above aren't enough"
                >
                  Advanced
                </summary>
                <div className="flex items-center gap-2 mt-2 flex-wrap">
                  <button
                    type="button"
                    onClick={handleBackfillIsins}
                    disabled={backfilling}
                    className="px-3 py-1 bg-amber/20 hover:bg-amber/30 disabled:opacity-40 text-amber-light rounded text-xs font-medium transition-colors"
                    title="Apply the curated name→ISIN map to funds I recognise (Fidelity Index, L&G, Vanguard, Baillie Gifford, etc)."
                  >
                    {backfilling ? 'Backfilling…' : 'Auto-fill known'}
                  </button>
                  <button
                    type="button"
                    onClick={handleFixCurrencies}
                    className="px-3 py-1 bg-amber/20 hover:bg-amber/30 text-amber-light rounded text-xs font-medium transition-colors"
                    title="Auto-detect currency from symbol/ISIN. Fixes holdings where the seed has the wrong currency tag (e.g. NVDA flagged GBP)."
                  >
                    Fix currencies
                  </button>
                </div>
              </details>
            </div>
          </div>
          {showStuckList && (
            <div className="mt-3 max-h-80 overflow-y-auto overflow-x-auto border-t border-amber/20 pt-2">
              <table className="w-full text-xs">
                <thead className="text-text-muted">
                  <tr>
                    <th className="text-left py-1">Account</th>
                    <th className="text-left py-1">Holding</th>
                    <th className="text-right py-1">Value (£)</th>
                  </tr>
                </thead>
                <tbody>
                  {stuckHoldings.slice(0, 100).map(h => (
                    <tr key={h.holding_id} className="border-t border-amber/10">
                      <td className="py-1 pr-2 text-text-secondary">{h.account_name}</td>
                      <td className="py-1 pr-2 text-text-primary">{h.name}</td>
                      <td className="py-1 text-right font-mono text-text-primary">
                        {Math.round(h.value_gbp).toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {stuckHoldings.length > 100 && (
                <div className="text-xs text-text-muted pt-2">
                  (+{stuckHoldings.length - 100} more not shown)
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Read-only banner for demo / viewer accounts */}
      {!canConfigure && (
        <div className="card p-3 border-blue/40 bg-blue/5 text-sm text-text-primary">
          <span className="font-semibold text-blue-light">Demo view.</span>{' '}
          You are logged in as a viewer — editing and imports are disabled.
        </div>
      )}

      {/* AJ Bell import result toast */}
      {ajbResult && (
        <div
          className={clsx(
            'card p-3 flex items-center gap-3 text-sm',
            ajbResult.ok ? 'border-emerald/40 bg-emerald/5' : 'border-rose/40 bg-rose/5',
          )}
        >
          <span className={ajbResult.ok ? 'text-emerald' : 'text-rose-light'}>
            {ajbResult.ok ? '✓' : '✗'}
          </span>
          <span className="text-text-primary flex-1">{ajbResult.message}</span>
          <button
            type="button"
            onClick={() => setAjbResult(null)}
            className="text-text-muted hover:text-text-primary text-xs"
            aria-label="Dismiss"
          >
            ✕
          </button>
        </div>
      )}

      {/* Non-blocking import reconciliation warning: the import APPLIED, but
          one or more accounts changed sharply (emptied / big value drop /
          lost half the positions). Advisory — ask the user to confirm before
          trusting the new figures. Renders only when flags are present. */}
      {ajbResult?.reconciliation && ajbResult.reconciliation.length > 0 && (
        <div className="card p-3 border-amber/50 bg-amber/5 flex flex-col gap-2 text-sm">
          <div className="flex items-start gap-2">
            <span className="text-amber shrink-0" aria-hidden>⚠</span>
            <span className="text-text-primary font-medium flex-1">
              Import applied, but {ajbResult.reconciliation.length} account
              {ajbResult.reconciliation.length !== 1 ? 's' : ''} changed sharply — please confirm the file was complete before trusting the new figures.
            </span>
          </div>
          <ul className="list-disc pl-7 space-y-1">
            {ajbResult.reconciliation.map((f, i) => (
              <li key={f.account_id ?? i} className="text-text-secondary">
                <span className="text-text-primary font-medium">
                  {f.account_name ?? `Account ${f.account_id ?? '?'}`}
                </span>
                {': '}
                {f.message ?? 'changed sharply on import'}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Discretionary summary banner */}
      {summary && (
        <div className="card p-4 flex flex-col sm:flex-row gap-4 sm:items-center sm:justify-between">
          <div className="flex items-center gap-3">
            <span className="inline-block w-2.5 h-2.5 rounded-full bg-emerald shrink-0" aria-hidden />
            <p className="text-sm text-text-primary">
              You can trade:{' '}
              <span className="font-bold text-emerald">{fmtGBP(summary.discretionary_value)}</span>
              <span className="text-text-muted ml-1">across {summary.discretionary_accounts} account{summary.discretionary_accounts !== 1 ? 's' : ''}</span>
            </p>
          </div>
          <div className="flex items-center gap-3">
            <span className="inline-block w-2.5 h-2.5 rounded-full bg-text-muted shrink-0" aria-hidden />
            <p className="text-sm text-text-primary">
              View only:{' '}
              <span className="font-bold text-text-secondary">{fmtGBP(summary.non_discretionary_value)}</span>
              <span className="text-text-muted ml-1">across {summary.non_discretionary_accounts} account{summary.non_discretionary_accounts !== 1 ? 's' : ''}</span>
            </p>
          </div>
          <div className="hidden sm:block w-32 shrink-0">
            <div className="h-2 bg-surface-hover rounded-full overflow-hidden flex" role="presentation">
              <div className="h-full bg-emerald" style={{ width: `${summary.discretionary_pct}%` }} />
              <div className="h-full bg-text-muted/40 flex-1" />
            </div>
            <p className="text-xs text-text-muted mt-1 text-right">{fmtPct(summary.discretionary_pct, 1)} discretionary</p>
          </div>
        </div>
      )}

      {/* Household summary KPIs */}
      {summary && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 stagger-children">
          <div className="stat-card sm:col-span-2">
            <div className="flex items-start justify-between gap-2">
              <p className="panel-title">Total Net Worth</p>
              <TrendBadge pct={trendPct30d} label="30d" size="sm" />
            </div>
            <p className="stat-hero text-text-primary">{fmtGBP(summary.total_net_worth)}</p>
            <p className="text-xs text-text-muted">
              {summary.total_accounts} account{summary.total_accounts !== 1 ? 's' : ''}, {summary.total_holdings} holdings
            </p>
          </div>

          {(summary.by_owner ?? []).map(o => (
            <div key={o.owner} className="stat-card">
              <p className="stat-label">{o.owner}</p>
              <p className="stat-value">{fmtGBP(o.total_value)}</p>
              <p className="text-xs text-text-muted">
                {o.account_count} account{o.account_count !== 1 ? 's' : ''}
              </p>
            </div>
          ))}

          {(summary.by_type ?? []).map(t => (
            <div key={t.account_type} className="stat-card">
              <p className="stat-label">{t.account_type}</p>
              <p className="stat-value">{fmtGBP(t.total_value)}</p>
              <p className="text-xs text-text-muted">
                {t.account_count} account{t.account_count !== 1 ? 's' : ''}
              </p>
            </div>
          ))}
        </div>
      )}

      {/* Provider pie chart */}
      {pieData.length > 0 && (
        <div className="card p-5">
          <h2 className="panel-title mb-4">Allocation by Provider</h2>
          <div className="grid md:grid-cols-2 gap-6 items-center">
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie
                  data={pieData}
                  cx="50%"
                  cy="50%"
                  innerRadius={55}
                  outerRadius={95}
                  dataKey="value"
                  nameKey="name"
                  paddingAngle={2}
                >
                  {pieData.map((entry, i) => (
                    <Cell key={i} fill={entry.color} stroke="transparent" />
                  ))}
                </Pie>
                <Tooltip
                  formatter={(v: number) => [fmtGBP(v), 'Value']}
                  contentStyle={{ background: 'rgb(var(--bg-surface))', border: '1px solid rgb(var(--border))', color: 'rgb(var(--text-primary))', borderRadius: 8 }}
                />
              </PieChart>
            </ResponsiveContainer>
            <div className="space-y-2">
              {pieData.map((d, i) => (
                <div key={i} className="flex items-center gap-3 text-sm">
                  <span className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: d.color }} aria-hidden />
                  <span className="flex-1 text-text-secondary">{d.name}</span>
                  <span className="font-mono text-text-primary">{fmtGBP(d.value)}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Account list */}
      <div className="space-y-3">
        <h2 className="panel-title">All Accounts</h2>
        {loading && accounts.length === 0 ? (
          <div className="card p-8 text-center text-sm text-text-muted">
            <RefreshCw size={20} className="animate-spin mx-auto mb-2 text-blue" aria-hidden />
            Loading accounts...
          </div>
        ) : accounts.length === 0 ? (
          <div className="card p-8 text-center text-sm text-text-muted">
            No accounts yet. Click "Add Account" to get started.
          </div>
        ) : (
          accounts.map(acc => <AccountRow key={acc.id} account={acc} trend={accountTrends[acc.name]} />)
        )}
      </div>
    </div>
  )
}
